import React, {useEffect, useMemo, useRef, useState} from "react";
import axios from "axios";
import {Tag, X, Plus} from "lucide-react";

import * as Constants from "../../utils/Constants";
import * as UrlConstants from "../../utils/UrlConstants";
import {getAuthToken, getClientIdOfUser, showToast} from "../../utils/utility";

import Modal from "../../components/ui/Modal";
import Button from "../../components/ui/Button";

/**
 * Per-asset label editor (redesign parity with the old LabelInput popup).
 *
 * Add existing labels from suggestions or type a new name to create one; remove
 * with the × on each pill. Saving POSTs the full label-name set to
 * `asset-labels/assign`, which replaces the asset's labels (creating any that
 * don't exist yet) and returns the canonical list back.
 */
export default function AssetLabelsModal({open, asset, allLabels = [], currentLabels = [], onClose, onSaved}) {
  // Working copy of the label names on this asset while the modal is open.
  const [names, setNames] = useState([]);
  const [input, setInput] = useState("");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  // Reseed from the asset's current labels each time the modal opens for a row.
  useEffect(() => {
    if (open) {
      setNames(currentLabels.map((l) => l.labelName));
      setInput("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, asset?.assetId]);

  const lowerNames = useMemo(() => names.map((n) => n.toLowerCase()), [names]);

  // Existing labels not already applied, filtered by the current input.
  const suggestions = useMemo(() => {
    const q = input.trim().toLowerCase();
    return allLabels
      .filter((l) => !lowerNames.includes(l.labelName.toLowerCase()))
      .filter((l) => (q ? l.labelName.toLowerCase().includes(q) : true))
      .slice(0, 8);
  }, [allLabels, lowerNames, input]);

  const addName = (raw) => {
    const name = (raw || "").trim();
    if (!name) return;
    if (lowerNames.includes(name.toLowerCase())) {
      setInput("");
      return;
    }
    setNames((prev) => [...prev, name]);
    setInput("");
    inputRef.current?.focus();
  };

  const removeName = (name) => setNames((prev) => prev.filter((n) => n !== name));

  const onInputKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addName(input);
    } else if (e.key === "Backspace" && !input && names.length) {
      // Quick-delete the last chip, matching common tag-input UX.
      removeName(names[names.length - 1]);
    }
  };

  const save = async () => {
    if (!asset) return;
    try {
      setSaving(true);
      const url = `${Constants.BASE_URL}${Constants.API_V1}/${getClientIdOfUser()}/${UrlConstants.ASSET_LABELS_ASSIGN}`;
      axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
      const resp = await axios.post(url, {assetId: asset.assetId, labelNames: names});
      if (resp.status === 200) {
        const saved = resp.data?.responseData || [];
        onSaved?.(asset.assetId, saved);
        showToast("Labels updated", {type: "success"});
        onClose?.();
      }
    } catch (err) {
      showToast(err?.response?.data?.responseMessage || "Could not update labels", {type: "error"});
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={(o) => !o && onClose?.()}
      title="Manage labels"
      subtitle={asset ? asset.name : undefined}
      size="md"
    >
      <Modal.Body>
        {/* Applied labels */}
        <div className="min-h-[2.5rem] flex flex-wrap items-center gap-2">
          {names.length === 0 ? (
            <span className="text-sm text-ink-400">No labels yet — add one below.</span>
          ) : (
            names.map((name) => (
              <span
                key={name}
                className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full bg-brand-100 text-brand-700 text-sm font-medium"
              >
                <Tag className="h-3.5 w-3.5" />
                {name}
                <button
                  type="button"
                  onClick={() => removeName(name)}
                  aria-label={`Remove ${name}`}
                  className="inline-flex items-center justify-center h-5 w-5 rounded-full text-brand-700 hover:bg-brand-200"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            ))
          )}
        </div>

        {/* Add input */}
        <div className="mt-4 flex items-center gap-2">
          <div className="relative flex-1">
            <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400" />
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onInputKeyDown}
              placeholder="Type a label and press Enter…"
              className="block w-full h-9 pl-9 pr-3 text-base rounded-md bg-white border border-ink-200 shadow-inset text-ink-900 hover:border-ink-300 focus:border-brand-500 focus:outline-none focus:ring focus:ring-offset-0"
            />
          </div>
          <Button variant="secondary" size="sm" icon={Plus} onClick={() => addName(input)} disabled={!input.trim()}>
            Add
          </Button>
        </div>

        {/* Suggestions from existing client labels */}
        {suggestions.length > 0 && (
          <div className="mt-3">
            <div className="text-xs font-medium text-ink-500 mb-1.5">Existing labels</div>
            <div className="flex flex-wrap gap-1.5">
              {suggestions.map((l) => (
                <button
                  key={l.labelId}
                  type="button"
                  onClick={() => addName(l.labelName)}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-ink-100 text-ink-700 text-sm hover:bg-brand-100 hover:text-brand-700"
                >
                  <Plus className="h-3 w-3" />
                  {l.labelName}
                </button>
              ))}
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="ghost" onClick={() => onClose?.()} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={save} loading={saving}>Save labels</Button>
      </Modal.Footer>
    </Modal>
  );
}
